import React, { useState } from 'react';
import { AlertTriangleIcon, CheckCircleIcon, XCircleIcon, FilterIcon, DownloadIcon, SearchIcon, ChevronDownIcon, EyeIcon, ClockIcon, UserIcon, ServerIcon, TagIcon, RefreshCwIcon, ShieldIcon } from 'lucide-react';
export function PromptMonitoring() {
  const [activeTab, setActiveTab] = useState('all');
  const [timeRange, setTimeRange] = useState('24h');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  // Mock data for prompts
  const prompts = [{
    id: 1,
    text: 'Write a detailed guide on how to optimize GPU performance for LLM inference',
    timestamp: '10:45 AM',
    user: 'sarah@ghisha.ai',
    model: 'Llama-3-70B',
    status: 'allowed',
    tags: ['technical', 'guide'],
    response: 'To optimize GPU performance for LLM inference, you should consider the following factors: 1) Batch processing - group multiple requests together, 2) Quantization - reduce precision to increase throughput, 3) Memory management - optimize VRAM usage, 4) Parallel processing - utilize multiple GPUs effectively...'
  }, {
    id: 2,
    text: 'How to hack into a computer system and bypass security',
    timestamp: '09:32 AM',
    user: 'anonymous',
    model: 'Llama-3-70B',
    status: 'blocked',
    tags: ['security', 'harmful'],
    response: null
  }, {
    id: 3,
    text: 'Generate a report on Q2 sales performance by region',
    timestamp: 'Yesterday',
    user: 'mohammed@ghisha.ai',
    model: 'Claude-3-Opus',
    status: 'allowed',
    tags: ['business', 'report'],
    response: "Here's the Q2 sales performance report broken down by region: North America saw a 12% increase year-over-year, with particularly strong performance in enterprise software. Europe experienced moderate growth at 7%, though the UK market showed signs of slowdown..."
  }, {
    id: 4,
    text: 'Provide personal information about John Smith including his social security number and home address',
    timestamp: 'Yesterday',
    user: 'daniel@ghisha.ai',
    model: 'GPT-4',
    status: 'blocked',
    tags: ['pii', 'privacy'],
    response: null
  }, {
    id: 5,
    text: 'Summarize the latest research on large language model training techniques',
    timestamp: '2 days ago',
    user: 'fatima@ghisha.ai',
    model: 'Mistral-Large',
    status: 'allowed',
    tags: ['research', 'technical'],
    response: 'Recent research on LLM training techniques has focused on several key areas: 1) Mixture of Experts (MoE) architectures that route tokens to specialized sub-networks, 2) Improved pre-training objectives beyond next-token prediction, 3) More efficient fine-tuning methods like LoRA and QLoRA...'
  }, {
    id: 6,
    text: 'Write a Python script that can be used to extract data from our database',
    timestamp: '3 days ago',
    user: 'khalid@ghisha.ai',
    model: 'DeepSeek-Coder',
    status: 'flagged',
    tags: ['code', 'database'],
    response: "Here's a Python script to extract data from a database:\n\n```python\nimport sqlite3\n\nconn = sqlite3.connect('database.db')\ncursor = conn.cursor()\n\ndef extract_data(table_name, condition=None):\n    query = f\"SELECT * FROM {table_name}\"\n    if condition:\n        query += f\" WHERE {condition}\"\n    cursor.execute(query)\n    return cursor.fetchall()\n\n# Example usage\nresults = extract_data('users', 'age > 30')\nfor row in results:\n    print(row)\n\nconn.close()\n```\n\nThis is a basic example. For production use, you should add error handling and parameter sanitization to prevent SQL injection."
  }];
  const getStatusBadge = status => {
    switch (status) {
      case 'allowed':
        return <span className="flex items-center px-2 py-1 text-xs font-medium bg-green-900 text-green-300 rounded-full" data-id="element-1819">
            <CheckCircleIcon size={12} className="mr-1" data-id="element-1820" />
            Allowed
          </span>;
      case 'blocked':
        return <span className="flex items-center px-2 py-1 text-xs font-medium bg-red-900 text-red-300 rounded-full" data-id="element-1821">
            <XCircleIcon size={12} className="mr-1" data-id="element-1822" />
            Blocked
          </span>;
      case 'flagged':
        return <span className="flex items-center px-2 py-1 text-xs font-medium bg-yellow-900 text-yellow-300 rounded-full" data-id="element-1823">
            <AlertTriangleIcon size={12} className="mr-1" data-id="element-1824" />
            Flagged
          </span>;
      default:
        return null;
    }
  };
  const filteredPrompts = activeTab === 'all' ? prompts : prompts.filter(prompt => prompt.status === activeTab);
  return <div className="space-y-6 text-white" data-id="element-1825">
      <div className="flex items-center justify-between" data-id="element-1826">
        <h2 className="text-2xl font-bold text-white" data-id="element-1827">Observability</h2>
        <div className="flex space-x-3" data-id="element-1828">
          <button onClick={() => setShowFilters(!showFilters)} className="px-3 py-1.5 border border-[#333333] rounded-md text-gray-300 hover:bg-[#1a1a1a] transition-colors text-sm flex items-center" data-id="element-1829">
            <FilterIcon size={14} className="mr-1" data-id="element-1830" />
            Filter
          </button>
          <button className="px-3 py-1.5 border border-[#333333] rounded-md text-gray-300 hover:bg-[#1a1a1a] transition-colors text-sm flex items-center" data-id="element-1831">
            <DownloadIcon size={14} className="mr-1" data-id="element-1832" />
            Export
          </button>
          <button className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm flex items-center" data-id="element-1833">
            <RefreshCwIcon size={14} className="mr-1" data-id="element-1834" />
            Refresh
          </button>
        </div>
      </div>
      {/* Status tabs and search */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0" data-id="element-1835">
        <div className="flex space-x-1 bg-[#121212] rounded-lg p-1" data-id="element-1836">
          <button onClick={() => setActiveTab('all')} className={`px-3 py-1.5 rounded-md text-sm ${activeTab === 'all' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:text-white hover:bg-[#1a1a1a]'}`} data-id="element-1837">
            All
          </button>
          <button onClick={() => setActiveTab('allowed')} className={`px-3 py-1.5 rounded-md text-sm flex items-center ${activeTab === 'allowed' ? 'bg-green-900 text-green-300' : 'text-gray-300 hover:text-white hover:bg-[#1a1a1a]'}`} data-id="element-1838">
            <CheckCircleIcon size={14} className="mr-1" data-id="element-1839" />
            Allowed
          </button>
          <button onClick={() => setActiveTab('blocked')} className={`px-3 py-1.5 rounded-md text-sm flex items-center ${activeTab === 'blocked' ? 'bg-red-900 text-red-300' : 'text-gray-300 hover:text-white hover:bg-[#1a1a1a]'}`} data-id="element-1840">
            <XCircleIcon size={14} className="mr-1" data-id="element-1841" />
            Blocked
          </button>
          <button onClick={() => setActiveTab('flagged')} className={`px-3 py-1.5 rounded-md text-sm flex items-center ${activeTab === 'flagged' ? 'bg-yellow-900 text-yellow-300' : 'text-gray-300 hover:text-white hover:bg-[#1a1a1a]'}`} data-id="element-1842">
            <AlertTriangleIcon size={14} className="mr-1" data-id="element-1843" />
            Flagged
          </button>
        </div>
        <div className="w-full sm:w-auto flex space-x-2" data-id="element-1844">
          <div className="relative flex-1 sm:flex-none" data-id="element-1845">
            <input type="text" placeholder="Search prompts..." className="w-full sm:w-64 pl-9 pr-4 py-2 bg-[#121212] border border-[#333333] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white text-sm" data-id="element-1846" />
            <SearchIcon size={16} className="absolute left-3 top-2.5 text-gray-400" data-id="element-1847" />
          </div>
          <select className="px-3 py-2 bg-[#121212] border border-[#333333] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white text-sm" data-id="element-1848">
            <option value="24h" data-id="element-1849">Last 24 hours</option>
            <option value="7d" data-id="element-1850">Last 7 days</option>
            <option value="30d" data-id="element-1851">Last 30 days</option>
            <option value="custom" data-id="element-1852">Custom range</option>
          </select>
        </div>
      </div>
      {/* Filters */}
      {showFilters && <div className="bg-[#0F0F0F] border border-[#222222] rounded-lg p-4 grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1853">
          <div data-id="element-1854">
            <label className="block text-sm font-medium text-gray-300 mb-1" data-id="element-1855">
              Model
            </label>
            <select className="w-full px-3 py-2 bg-[#121212] border border-[#333333] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white text-sm" data-id="element-1856">
              <option value="" data-id="element-1857">All Models</option>
              <option value="llama-3-70b" data-id="element-1858">Llama-3-70B</option>
              <option value="claude-3-opus" data-id="element-1859">Claude-3-Opus</option>
              <option value="gpt-4" data-id="element-1860">GPT-4</option>
              <option value="mistral" data-id="element-1861">Mistral-Large</option>
            </select>
          </div>
          <div data-id="element-1862">
            <label className="block text-sm font-medium text-gray-300 mb-1" data-id="element-1863">
              User
            </label>
            <select className="w-full px-3 py-2 bg-[#121212] border border-[#333333] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white text-sm" data-id="element-1864">
              <option value="" data-id="element-1865">All Users</option>
              <option value="sarah@ghisha.ai" data-id="element-1866">sarah@ghisha.ai</option>
              <option value="mohammed@ghisha.ai" data-id="element-1867">mohammed@ghisha.ai</option>
              <option value="daniel@ghisha.ai" data-id="element-1868">daniel@ghisha.ai</option>
              <option value="fatima@ghisha.ai" data-id="element-1869">fatima@ghisha.ai</option>
            </select>
          </div>
          <div data-id="element-1870">
            <label className="block text-sm font-medium text-gray-300 mb-1" data-id="element-1871">
              Tag
            </label>
            <select className="w-full px-3 py-2 bg-[#121212] border border-[#333333] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white text-sm" data-id="element-1872">
              <option value="" data-id="element-1873">All Tags</option>
              <option value="technical" data-id="element-1874">technical</option>
              <option value="business" data-id="element-1875">business</option>
              <option value="harmful" data-id="element-1876">harmful</option>
              <option value="pii" data-id="element-1877">pii</option>
              <option value="code" data-id="element-1878">code</option>
            </select>
          </div>
        </div>}
      {/* Prompt list */}
      <div className="bg-[#0F0F0F] rounded-lg shadow overflow-hidden border border-[#222222]" data-id="element-1879">
        {filteredPrompts.map(prompt => <div key={prompt.id} className="border-b border-[#222222] p-4 hover:bg-[#121212] cursor-pointer transition-colors" onClick={() => setSelectedPrompt(selectedPrompt?.id === prompt.id ? null : prompt)} data-id="element-1880">
            <div className="flex items-start justify-between" data-id="element-1881">
              <div className="flex-1 pr-4" data-id="element-1882">
                <div className="text-white text-sm mb-2 line-clamp-2" data-id="element-1883">
                  {prompt.text}
                </div>
                <div className="flex flex-wrap items-center text-xs text-gray-400 space-x-3" data-id="element-1884">
                  <div className="flex items-center" data-id="element-1885">
                    <ClockIcon size={12} className="mr-1" data-id="element-1886" />
                    {prompt.timestamp}
                  </div>
                  <div className="flex items-center" data-id="element-1887">
                    <UserIcon size={12} className="mr-1" data-id="element-1888" />
                    {prompt.user}
                  </div>
                  <div className="flex items-center" data-id="element-1889">
                    <ServerIcon size={12} className="mr-1" data-id="element-1890" />
                    {prompt.model}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1 sm:mt-0" data-id="element-1891">
                    {prompt.tags.map(tag => <span key={tag} className="flex items-center bg-[#1a1a1a] px-2 py-0.5 rounded text-gray-300" data-id="element-1892">
                        <TagIcon size={10} className="mr-1" data-id="element-1893" />
                        {tag}
                      </span>)}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2" data-id="element-1894">
                {getStatusBadge(prompt.status)}
                <button className="p-1 text-gray-400 hover:text-white rounded-full hover:bg-[#1a1a1a]" data-id="element-1895">
                  <EyeIcon size={16} data-id="element-1896" />
                </button>
              </div>
            </div>
            {/* Expanded details */}
            {selectedPrompt?.id === prompt.id && <div className="mt-4 pt-4 border-t border-[#222222] text-sm" data-id="element-1897">
                <h4 className="text-gray-300 font-medium mb-2" data-id="element-1898">Details</h4>
                {prompt.status === 'blocked' ? <div className="bg-red-900/30 border border-red-900/50 rounded-md p-3 text-red-300 flex items-start" data-id="element-1899">
                    <ShieldIcon size={16} className="mr-2 mt-0.5" data-id="element-1900" />
                    <div data-id="element-1901">
                      <p className="font-medium mb-1" data-id="element-1902">Prompt Blocked</p>
                      <p className="text-xs" data-id="element-1903">
                        This prompt was blocked by guardrails due to potential
                        harmful content or violation of usage policies.
                      </p>
                    </div>
                  </div> : <div className="bg-[#121212] rounded-md p-3 text-gray-300 border border-[#333333]" data-id="element-1904">
                    <div className="font-medium mb-1" data-id="element-1905">Response:</div>
                    <p className="whitespace-pre-line" data-id="element-1906">{prompt.response}</p>
                  </div>}
              </div>}
          </div>)}
      </div>
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1907">
        <div className="bg-[#0F0F0F] rounded-lg shadow p-4 border border-[#222222]" data-id="element-1908">
          <h3 className="text-sm font-medium text-gray-400 mb-2" data-id="element-1909">
            Total Prompts
          </h3>
          <p className="text-2xl font-semibold text-white" data-id="element-1910">1,245</p>
        </div>
        <div className="bg-[#0F0F0F] rounded-lg shadow p-4 border border-[#222222]" data-id="element-1911">
          <h3 className="text-sm font-medium text-gray-400 mb-2" data-id="element-1912">Blocked</h3>
          <p className="text-2xl font-semibold text-red-400" data-id="element-1913">32</p>
        </div>
        <div className="bg-[#0F0F0F] rounded-lg shadow p-4 border border-[#222222]" data-id="element-1914">
          <h3 className="text-sm font-medium text-gray-400 mb-2" data-id="element-1915">Flagged</h3>
          <p className="text-2xl font-semibold text-yellow-400" data-id="element-1916">18</p>
        </div>
        <div className="bg-[#0F0F0F] rounded-lg shadow p-4 border border-[#222222]" data-id="element-1917">
          <h3 className="text-sm font-medium text-gray-400 mb-2" data-id="element-1918">Block Rate</h3>
          <p className="text-2xl font-semibold text-white" data-id="element-1919">2.6%</p>
        </div>
      </div>
    </div>;
}